import React, { useState, useMemo } from 'react';
import { resetPassword } from '@/services/users';
import { CustomLink } from '@/components/navigation';
import { Trans } from '@lingui/react/macro';
import { ValidationSummary, ValidationForm } from '@/components/forms/validation';
import { useValidationManager, useQueryString } from '@/utils/hooks';
import { withRouter, RouteComponentProps } from 'react-router';
import UserPageInput from './UserPageInput';
import SplashPage from './SplashPage';
import { TranslatableText } from '@/types';
import { PasswordValidator, getDefaultPasswordRequirements } from '@/utils/password';
import PasswordValidation from './PasswordValidation';
import { Button as RubusButton } from '@ygtechnologies/rubus-ui';
import { Toast } from '@ygtechnologies/bygr-frontend-component-library';

type OwnProps = RouteComponentProps<any> & {};

interface QueryStringParams {
    token: string;
}

const ResetPassword: React.FC<OwnProps> = ({ location }) => {
    const [externalErrors, setExternalErrors] = useState<TranslatableText[]>([]);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const { token } = useQueryString<QueryStringParams>(location);
    const validator = useMemo(() => new PasswordValidator(getDefaultPasswordRequirements()), []);

    const validationManager = useValidationManager([
        {
            isValid: () => password === confirmPassword,
            message: () => <Trans>Passwords must match</Trans>,
            summaryMessage: () => <Trans>Passwords must match</Trans>,
        },
        {
            isValid: (value, options) => validator.validate(password).valid,
            message: ({ field }) => <Trans>Password does not fulfill complexity rules</Trans>,
            summaryMessage: ({ field }) => <Trans>Password does not fulfill complexity rules</Trans>,
        },
    ]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            await resetPassword(token, password);
            setSuccess(true);
        } catch (e: any) {
            if (e.status === 400) {
                Toast.error(<Trans>This invitation link is either invalid, or has already been used.</Trans>, {
                    autoClose: false,
                });
            }
            setExternalErrors([<Trans>This invitation link is either invalid, or has already been used.</Trans>]);
        } finally {
            setSaving(false);
        }
    };

    if (success) {
        return (
            <SplashPage>
                <h3>
                    <Trans>Your password was reset.</Trans>
                </h3>
                <CustomLink to="/login">
                    <Trans>Continue to sign in</Trans>
                </CustomLink>
            </SplashPage>
        );
    }

    return (
        <SplashPage>
            <h3>
                <Trans>Create a new password</Trans>
            </h3>

            <ValidationForm validationManager={validationManager} onSubmit={handleFormSubmit}>
                <UserPageInput
                    required
                    type="password"
                    label={<Trans>New password</Trans>}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                />

                <UserPageInput
                    required
                    type="password"
                    label={<Trans>Confirm new password</Trans>}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                />

                <PasswordValidation password={password} validator={validator} />

                <ValidationSummary externalErrors={externalErrors} />

                <RubusButton size="lg" iconPosition="right" type="submit" variant="primary" loading={saving} block>
                    <Trans>Reset password</Trans>
                </RubusButton>
            </ValidationForm>
        </SplashPage>
    );
};

export default withRouter(ResetPassword);
